import {DOCUMENT} from '@angular/common';
import {Inject, Injectable, OnDestroy} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';

type AnalyticsWindow = Window & {
    dataLayer?: IArguments[];
    gtag?: (...args: any[]) => void;
};

@Injectable({providedIn: 'root'})
export class AnalyticsService implements OnDestroy {
    private readonly measurementId = 'G-10NEL0DTK8';
    private subscription: Subscription;
    private initialized = false;
    private lastPage: string;

    constructor(@Inject(DOCUMENT) private document: Document, private router: Router) {}

    initialize(): void {
        const win = this.allowedWindow();
        if (!win || this.initialized) {
            return;
        }
        this.initialized = true;
        win.dataLayer = win.dataLayer || [];
        win.gtag = win.gtag || function () { win.dataLayer.push(arguments); };
        win.gtag('js', new Date());
        // Angular owns pageviews. Also disable history-based pageviews in GA4
        // Enhanced Measurement (see docs/analytics.md).
        win.gtag('config', this.measurementId, {send_page_view: false});

        const script = this.document.createElement('script');
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=' + this.measurementId;
        this.document.head.appendChild(script);

        this.subscription = this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.trackPage(event.urlAfterRedirects);
            }
        });
        if (this.router.navigated) {
            this.trackPage(this.router.url);
        }
    }

    private allowedWindow(): AnalyticsWindow | null {
        const win = this.document.defaultView as AnalyticsWindow | null;
        return win && (win.location.hostname === 'ngenioconnect.com' ||
            win.location.hostname === 'www.ngenioconnect.com') ? win : null;
    }

    private trackPage(path: string): void {
        const win = this.allowedWindow();
        if (!win || !win.gtag) {
            return;
        }
        const page = win.location.origin + path;
        if (page === this.lastPage) {
            return;
        }
        win.gtag('event', 'page_view', {
            send_to: this.measurementId,
            page_location: page,
            page_title: this.document.title,
            page_referrer: this.lastPage || this.document.referrer
        });
        this.lastPage = page;
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
}
